import { useState } from "react";
import { useCurrentUser, useUpdateProfile, useChangePassword } from "../../../lib/api/users.api";
import { Modal } from "../../../ui/molecules/Modal";
import { PassphraseInput } from "../../auth/components/PassphraseInput";

const MIN_WORDS = 4;
const MIN_WORD_LENGTH = 3;
const MIN_PASSWORD = 12;

type PasswordMode = "passphrase" | "classic";

function isPassphraseValid(words: readonly string[]): boolean {
  return words.filter((w) => w.length >= MIN_WORD_LENGTH).length >= MIN_WORDS;
}

export function AccountSettings({ onClose }: { readonly onClose: () => void }) {
  const { data: user } = useCurrentUser();

  if (!user) return null;

  return (
    <Modal
      title="Mon compte"
      description="Gérez vos informations personnelles"
      onClose={onClose}
      maxWidth="2xl"
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-xs font-medium border border-border/60 bg-surface text-ink hover:bg-surface-alt rounded-md transition-colors"
          >
            Fermer
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <NameSection currentName={user.name} />
        <div className="border-t border-border pt-6">
          <PasswordSection />
        </div>
      </div>
    </Modal>
  );
}

function NameSection({ currentName }: { readonly currentName: string }) {
  const [name, setName] = useState(currentName);
  const [saved, setSaved] = useState(false);
  const updateProfile = useUpdateProfile();

  const hasChanged = name.trim() !== currentName;

  const handleSave = () => {
    if (!hasChanged || !name.trim()) return;
    updateProfile.mutate(
      { name: name.trim() },
      {
        onSuccess: () => {
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium text-ink">Prénom</h3>
      <div className="flex gap-2">
        <input
          id="account-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
          className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-surface-alt
            focus:outline-none focus:ring-2 focus:ring-accent/50 transition-shadow"
          placeholder="Votre prénom"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanged || !name.trim() || updateProfile.isPending}
          className="px-4 py-2 text-xs font-medium bg-accent text-white rounded-lg
            hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          {saved ? "Enregistré !" : updateProfile.isPending ? "..." : "Modifier"}
        </button>
      </div>
      {updateProfile.isError && (
        <p className="text-xs text-red-500">Erreur lors de la mise à jour.</p>
      )}
    </div>
  );
}

function PasswordSection() {
  const [currentMode, setCurrentMode] = useState<PasswordMode>("passphrase");
  const [currentPassphraseWords, setCurrentPassphraseWords] = useState<string[]>([]);
  const [currentClassicPassword, setCurrentClassicPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);

  const [mode, setMode] = useState<PasswordMode>("passphrase");
  const [passphraseWords, setPassphraseWords] = useState<string[]>([]);
  const [classicPassword, setClassicPassword] = useState("");
  const [showClassic, setShowClassic] = useState(false);
  const [success, setSuccess] = useState(false);
  const changePassword = useChangePassword();

  const getCurrentPassword = () =>
    currentMode === "passphrase" ? currentPassphraseWords.join(" ") : currentClassicPassword;

  const hasCurrentPassword = currentMode === "passphrase"
    ? currentPassphraseWords.filter((w) => w.length >= 1).length >= 1
    : currentClassicPassword.length >= 1;

  const hasNewPassword = mode === "passphrase"
    ? isPassphraseValid(passphraseWords)
    : classicPassword.length >= MIN_PASSWORD;

  const isValid = hasCurrentPassword && hasNewPassword;

  const getFinalPassword = () =>
    mode === "passphrase" ? passphraseWords.join(" ") : classicPassword;

  const handleSubmit = () => {
    if (!isValid) return;
    changePassword.mutate(
      { currentPassword: getCurrentPassword(), newPassword: getFinalPassword() },
      {
        onSuccess: () => {
          setCurrentPassphraseWords([]);
          setCurrentClassicPassword("");
          setPassphraseWords([]);
          setClassicPassword("");
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-medium text-ink">Changer le mot de passe</h3>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
        <p className="text-[11px] text-amber-400 leading-relaxed">
          Assurez-vous d'avoir votre clé de récupération avant de changer votre mot de passe. Sans elle, vous ne pourrez pas récupérer vos données en cas d'oubli.
        </p>
      </div>

      {/* Current password */}
      <div>
        <label className="text-xs font-medium text-ink-muted mb-2 block">
          Mot de passe actuel
        </label>
        <div className="flex rounded-lg border border-border overflow-hidden mb-3">
          <button
            type="button"
            onClick={() => { setCurrentMode("passphrase"); setCurrentClassicPassword(""); }}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              currentMode === "passphrase"
                ? "bg-accent text-white"
                : "bg-surface-alt text-ink-muted hover:text-ink"
            }`}
          >
            Phrase de passe
          </button>
          <button
            type="button"
            onClick={() => { setCurrentMode("classic"); setCurrentPassphraseWords([]); }}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              currentMode === "classic"
                ? "bg-accent text-white"
                : "bg-surface-alt text-ink-muted hover:text-ink"
            }`}
          >
            Mot de passe
          </button>
        </div>

        {currentMode === "passphrase" ? (
          <PassphraseInput
            words={currentPassphraseWords}
            onChange={setCurrentPassphraseWords}
            minWords={MIN_WORDS}
            autoComplete="current-password"
          />
        ) : (
          <div className="relative">
            <input
              type={showCurrent ? "text" : "password"}
              value={currentClassicPassword}
              onChange={(e) => setCurrentClassicPassword(e.target.value)}
              placeholder="Mot de passe actuel"
              autoComplete="current-password"
              className="w-full px-3 py-2 pr-16 text-sm rounded-lg border border-border bg-surface-alt
                focus:outline-none focus:ring-2 focus:ring-accent/50 placeholder:text-ink-faint transition-shadow"
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink text-xs font-medium transition-colors"
              tabIndex={-1}
            >
              {showCurrent ? "Masquer" : "Voir"}
            </button>
          </div>
        )}
      </div>

      {/* New password */}
      <div>
        <label className="text-xs font-medium text-ink-muted mb-2 block">
          Nouveau mot de passe
        </label>
        <div className="flex rounded-lg border border-border overflow-hidden mb-3">
          <button
            type="button"
            onClick={() => { setMode("passphrase"); setClassicPassword(""); }}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              mode === "passphrase"
                ? "bg-accent text-white"
                : "bg-surface-alt text-ink-muted hover:text-ink"
            }`}
          >
            Phrase de passe
          </button>
          <button
            type="button"
            onClick={() => { setMode("classic"); setPassphraseWords([]); }}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              mode === "classic"
                ? "bg-accent text-white"
                : "bg-surface-alt text-ink-muted hover:text-ink"
            }`}
          >
            Mot de passe
          </button>
        </div>

        {mode === "passphrase" ? (
          <PassphraseInput
            words={passphraseWords}
            onChange={setPassphraseWords}
            minWords={MIN_WORDS}
            autoComplete="new-password"
          />
        ) : (
          <div>
            <div className="relative">
              <input
                type={showClassic ? "text" : "password"}
                value={classicPassword}
                onChange={(e) => setClassicPassword(e.target.value)}
                placeholder="Nouveau mot de passe"
                autoComplete="new-password"
                className="w-full px-3 py-2 pr-16 text-sm rounded-lg border border-border bg-surface-alt
                  focus:outline-none focus:ring-2 focus:ring-accent/50 placeholder:text-ink-faint transition-shadow"
              />
              <button
                type="button"
                onClick={() => setShowClassic((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink text-xs font-medium transition-colors"
                tabIndex={-1}
              >
                {showClassic ? "Masquer" : "Voir"}
              </button>
            </div>
            {classicPassword.length > 0 && (
              <p className={`mt-1.5 text-[10px] font-medium ${classicPassword.length >= MIN_PASSWORD ? "text-emerald-500" : "text-ink-muted"}`}>
                {classicPassword.length}/{MIN_PASSWORD} caractères minimum
              </p>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isValid || changePassword.isPending}
        className="self-start px-4 py-2 text-xs font-medium bg-accent text-white rounded-lg
          hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
      >
        {changePassword.isPending ? "Modification..." : "Changer le mot de passe"}
      </button>

      {success && (
        <p className="text-xs text-emerald-500 font-medium">Mot de passe modifié avec succès !</p>
      )}

      {changePassword.isError && (
        <p className="text-xs text-red-500">
          {(() => {
            const status = (changePassword.error as { status?: number }).status;
            if (status === 403) return "Mot de passe actuel incorrect.";
            if (status === 400) return "Le nouveau mot de passe est trop faible.";
            return "Erreur lors du changement.";
          })()}
        </p>
      )}
    </div>
  );
}
